import { useEffect, useState } from "react";
import { supabase } from "../providers/supabase/supabase";

/**
 * Marca do escritório na tela de login (migration 291). Antes de entrar não há
 * organização: vale o endereço do escritório (?escritorio=<slug>) ou a última
 * marca deste navegador, guardada depois de um login de escritório cliente.
 * Sem nenhum dos dois, a marca da North.
 */

export interface MarcaDoLogin {
  nome: string;
  logo_url: string | null;
  cor_primaria: string | null;
}

const CHAVE = "north.marca-login";
const COR = /^#[0-9a-f]{6}$/i;

const ler = (): MarcaDoLogin | null => {
  try {
    const m = JSON.parse(localStorage.getItem(CHAVE) ?? "null");
    return m && typeof m.nome === "string" ? m : null;
  } catch {
    return null;
  }
};

/** Guarda (escritório cliente) ou apaga (North) a marca do próximo login. */
export const lembrarMarcaDoLogin = (marca: MarcaDoLogin | null) => {
  try {
    if (marca) localStorage.setItem(CHAVE, JSON.stringify(marca));
    else localStorage.removeItem(CHAVE);
  } catch {
    // Navegador sem armazenamento: o login fica com a marca da North.
  }
};

/** ?escritorio= na busca ou no hash (/#/login?escritorio=...). */
export const slugDoEndereco = (loc: Pick<Location, "search" | "hash"> = window.location): string | null => {
  const doHash = loc.hash.includes("?") ? loc.hash.slice(loc.hash.indexOf("?")) : "";
  const v = new URLSearchParams(loc.search).get("escritorio") ?? new URLSearchParams(doHash).get("escritorio");
  return v && /^[a-z0-9-]{1,80}$/.test(v) ? v : null;
};

/** Endereço de acesso do escritório, para a tela de Organizações. */
export const enderecoDeAcesso = (slug: string) => `${window.location.origin}/#/login?escritorio=${slug}`;

const valida = (m: MarcaDoLogin): MarcaDoLogin => ({
  nome: m.nome,
  logo_url: m.logo_url?.startsWith("https://") ? m.logo_url : null,
  cor_primaria: m.cor_primaria && COR.test(m.cor_primaria) ? m.cor_primaria : null,
});

/** undefined enquanto a marca do endereço não chega (nem a da North aparece). */
export const useMarcaDoLogin = (): MarcaDoLogin | null | undefined => {
  const [marca, setMarca] = useState<MarcaDoLogin | null | undefined>(() => {
    if (slugDoEndereco()) return undefined;
    const m = ler();
    return m ? valida(m) : null;
  });
  useEffect(() => {
    const slug = slugDoEndereco();
    if (!slug) return;
    let vivo = true;
    void supabase
      .rpc("marca_da_organizacao" as never, { p_slug: slug } as never)
      .then(({ data }) => {
        if (!vivo) return;
        const m = (data as unknown as MarcaDoLogin[] | null)?.[0];
        if (m) lembrarMarcaDoLogin(m);
        setMarca(m ? valida(m) : null);
      }, () => vivo && setMarca(null));
    return () => {
      vivo = false;
    };
  }, []);
  return marca;
};
